import React, { useState } from 'react';
import { 
  multiFactor, 
  TotpMultiFactorGenerator,
  TotpSecret,
  reauthenticateWithCredential,
  EmailAuthProvider,
  sendEmailVerification
} from 'firebase/auth';
import { auth } from '../../lib/firebase';
import QRCode from 'qrcode';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';
import { XMarkIcon, ShieldCheckIcon, QrCodeIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

interface MFASetupProps {
  onComplete: () => void;
  onCancel: () => void;
}

const MFASetup: React.FC<MFASetupProps> = ({ onComplete, onCancel }) => {
  const { t } = useLanguage();
  const [step, setStep] = useState<'info' | 'generate' | 'verify' | 'reauth'>('info');
  const [totpSecret, setTotpSecret] = useState<TotpSecret | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');

  const handleReauthenticate = async () => {
    try {
      setLoading(true);
      setError('');

      const user = auth.currentUser;
      if (!user || !user.email) {
        throw new Error('Nincs bejelentkezett felhasználó');
      }

      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
      
      setPassword('');
      setStep('generate');
      
      // Automatikusan próbáljuk újra a QR kód generálást
      await generateQRCode();
    } catch (err: any) {
      console.error('Error reauthenticating:', err);
      if (err.code === 'auth/wrong-password') {
        setError(t('mfa.setup.messages.wrongPassword'));
      } else {
        setError(t('mfa.setup.messages.error') + ': ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const generateQRCode = async () => {
    try {
      setLoading(true);
      setError('');

      const user = auth.currentUser;
      if (!user) {
        throw new Error('Nincs bejelentkezett felhasználó');
      }

      const multiFactorSession = await multiFactor(user).getSession();
      
      const totpSecret = await TotpMultiFactorGenerator.generateSecret(multiFactorSession);
      
      const otpAuthUrl = totpSecret.generateQrCodeUrl(
        user.email || 'YouCRM',
        'YouCRM'
      );

      // Generate QR code as base64 image
      const qrCodeDataUrl = await QRCode.toDataURL(otpAuthUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      setTotpSecret(totpSecret);
      setQrCodeUrl(qrCodeDataUrl);
      setStep('verify');
    } catch (err: any) {
      console.error('Error generating QR code:', err);
      if (err.code === 'auth/requires-recent-login') {
        setError(t('mfa.setup.messages.reauthRequired'));
        setStep('reauth');
      } else if (err.code === 'auth/quota-exceeded') {
        setError('⏳ Túl sok próbálkozás! Kérlek várj 15-30 percet és próbáld újra később.');
      } else {
        setError(t('mfa.setup.messages.error') + ': ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnableMFA = async () => {
    try {
      setLoading(true);
      setError('');

      if (!totpSecret) {
        throw new Error('Nincs TOTP secret');
      }

      const user = auth.currentUser;
      if (!user) {
        throw new Error('Nincs bejelentkezett felhasználó');
      }

      const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(
        totpSecret,
        verificationCode
      );

      await multiFactor(user).enroll(
        multiFactorAssertion,
        'Authenticator App'
      );

      toast.success(t('mfa.setup.messages.success'), {
        icon: '🔒',
        duration: 5000,
      });
      
      toast(t('mfa.setup.messages.successDescription'), {
        icon: '🛡️',
        duration: 6000,
      });

      onComplete();
    } catch (err: any) {
      console.error('Error verifying MFA:', err);
      if (err.code === 'auth/invalid-verification-code') {
        setError(t('mfa.setup.messages.invalidCode'));
      } else if (err.code === 'auth/quota-exceeded') {
        setError('⏳ Túl sok próbálkozás! Kérlek várj 15-30 percet és próbáld újra később.');
      } else {
        setError(t('mfa.setup.messages.error') + ': ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(t('mfa.setup.qrCode.keyCopied'), {
      icon: '📋',
      duration: 2000,
    });
  };

  const handleResendVerificationEmail = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error('Nincs bejelentkezett felhasználó');
        return;
      }

      if (user.emailVerified) {
        toast.success('Az email címed már megerősítve van! ✅');
        return;
      }

      await sendEmailVerification(user);
      toast.success('Megerősítő email elküldve! Ellenőrizd a postafiókod.', {
        duration: 6000,
        icon: '📧'
      });
    } catch (error: any) {
      console.error('Error sending verification email:', error);
      
      if (error.code === 'auth/too-many-requests') {
        toast.error('Túl sok próbálkozás! Várj néhány percet és próbáld újra.');
      } else {
        toast.error('Hiba a megerősítő email küldése során');
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6 relative animate-in fade-in duration-200">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl">
              <ShieldCheckIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('mfa.setup.title')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('mfa.setup.subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Email Verification Warning - csak email felhasználóknak */}
        {auth.currentUser && 
         auth.currentUser.providerData.some(p => p.providerId === 'password') && 
         !auth.currentUser.emailVerified && (
          <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl">
            <div className="flex items-start space-x-3">
              <svg className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300 mb-2">
                  ⚠️ Email cím nincs megerősítve
                </p>
                <p className="text-xs text-yellow-700 dark:text-yellow-400 mb-3">
                  Az MFA beállításához először meg kell erősítened az email címedet. Kattints az alábbi gombra, hogy újra küldjük a megerősítő emailt.
                </p>
                <button
                  onClick={handleResendVerificationEmail}
                  className="w-full px-3 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-2"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>Megerősítő email küldése</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl animate-in slide-in-from-top duration-200">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Step: Info */}
        {step === 'info' && (
          <div className="space-y-4">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
              <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-2 flex items-center">
                <QrCodeIcon className="h-5 w-5 mr-2" />
                {t('mfa.setup.info.title')}
              </h3>
              <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1.5">
                <li className="flex items-start">
                  <CheckCircleIcon className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Google Authenticator</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Microsoft Authenticator</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>TOTP kompatibilis app</span>
                </li>
              </ul>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {t('mfa.setup.info.description')}
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={generateQRCode}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white px-4 py-3 rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary-500/30"
              >
                {loading ? t('mfa.setup.buttons.generating') : t('mfa.setup.buttons.generateQR')}
              </button>
              <button
                onClick={onCancel}
                disabled={loading}
                className="px-4 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all font-medium"
              >
                {t('mfa.setup.buttons.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Step: Re-authentication */}
        {step === 'reauth' && (
          <div className="space-y-4">
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border border-orange-200 dark:border-orange-800 rounded-xl p-4">
              <h3 className="font-semibold text-orange-900 dark:text-orange-300 mb-2">
                {t('mfa.setup.reauth.title')}
              </h3>
              <p className="text-sm text-orange-800 dark:text-orange-300">
                {t('mfa.setup.reauth.description')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('mfa.setup.reauth.passwordLabel')}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && password && handleReauthenticate()}
                placeholder={t('mfa.setup.reauth.passwordPlaceholder')}
                className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleReauthenticate}
                disabled={loading || !password}
                className="flex-1 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white px-4 py-3 rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary-500/30"
              >
                {loading ? t('mfa.setup.buttons.verifying') : t('mfa.setup.buttons.continue')}
              </button>
              <button
                onClick={onCancel}
                disabled={loading}
                className="px-4 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all font-medium"
              >
                {t('mfa.setup.buttons.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Step: Verify */}
        {step === 'verify' && (
          <div className="space-y-4">
            <div className="bg-white dark:bg-gray-700 p-4 rounded-xl border-2 border-gray-200 dark:border-gray-600">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 text-center">
                {t('mfa.setup.qrCode.description')}
              </p>
              {qrCodeUrl && (
                <div className="flex justify-center">
                  <img 
                    src={qrCodeUrl} 
                    alt="QR Code" 
                    className="w-64 h-64 border-4 border-white dark:border-gray-800 rounded-xl shadow-lg"
                  />
                </div>
              )}
            </div>

            {totpSecret && (
              <div className="bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
                <p className="text-xs text-yellow-800 dark:text-yellow-300 font-semibold mb-2">
                  {t('mfa.setup.qrCode.manualKey')}
                </p>
                <div className="flex items-center space-x-2">
                  <code className="flex-1 text-xs text-yellow-900 dark:text-yellow-200 break-all bg-yellow-100 dark:bg-yellow-900/30 px-3 py-2 rounded-lg font-mono">
                    {totpSecret.secretKey}
                  </code>
                  <button
                    onClick={() => copyToClipboard(totpSecret.secretKey)}
                    className="px-3 py-2 bg-yellow-200 dark:bg-yellow-800 hover:bg-yellow-300 dark:hover:bg-yellow-700 text-yellow-900 dark:text-yellow-100 rounded-lg text-xs font-medium transition-colors"
                  >
                    {t('mfa.setup.qrCode.copyKey')}
                  </button>
                </div>
                <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-2">
                  {t('mfa.setup.qrCode.manualKeyDescription')}
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('mfa.setup.verify.title')}
              </label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && verificationCode.length === 6) {
                    verifyAndEnableMFA();
                  }
                }}
                placeholder={t('mfa.setup.verify.placeholder')}
                maxLength={6}
                autoFocus
                className="w-full px-4 py-4 text-center text-3xl font-mono border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all tracking-widest"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                {t('mfa.setup.verify.codeRefreshes')}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={verifyAndEnableMFA}
                disabled={loading || verificationCode.length !== 6}
                className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-4 py-3 rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-green-500/30"
              >
                {loading ? t('mfa.setup.buttons.verifying') : t('mfa.setup.buttons.enable')}
              </button>
              <button
                onClick={() => setStep('info')}
                disabled={loading}
                className="px-4 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all font-medium"
              >
                {t('mfa.setup.buttons.back')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MFASetup;
